import { useEffect, useMemo, useRef } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { useFilm } from '../context/FilmContext';
import { useScopedNav } from '../hooks/useScopedNav';

/**
 * URL patterns we try in priority order to extract :workspaceId and
 * :filmId. matchPath in react-router v6 needs the literal pattern with
 * `:param` placeholders. The first match wins; deeper patterns first
 * so we capture filmId before falling back to ws-only.
 */
const PATTERNS = [
  '/workspaces/:workspaceId/films/:filmId/*',
  '/workspaces/:workspaceId/films/:filmId',
  '/workspaces/:workspaceId/*',
  '/workspaces/:workspaceId',
  // Legacy routes — filmId only, workspace inferred elsewhere.
  '/films/:filmId/*',
  '/films/:filmId',
];

function extractIdsFromPath(pathname) {
  for (const pattern of PATTERNS) {
    const end = !pattern.endsWith('/*');
    const matched = matchPath({ path: pattern, end }, pathname);
    if (matched) {
      return {
        workspaceId: matched.params?.workspaceId || null,
        filmId: matched.params?.filmId || null,
      };
    }
  }
  return { workspaceId: null, filmId: null };
}

/**
 * RouteContextBridge — invisible component that does one-way URL → context sync.
 *
 * Must live inside <BrowserRouter> (it uses useLocation). On every
 * navigation it inspects the URL for :workspaceId / :filmId and pushes
 * those values into the Workspace / Film contexts when they differ.
 *
 * Critically: when the URL has NO workspace/film params (e.g. /calendar,
 * /gear, /files, /), the bridge leaves the context untouched. This is
 * the core "context is sticky across plain account-level routes" fix.
 *
 * The bridge never navigates — UI components (Phase 2 / Phase 3) read
 * context via useScopedNav and decide where to route.
 *
 * Also publishes a read-only snapshot at `window.__frameflowCtx` for
 * tester / DevTools introspection.
 */
export default function RouteContextBridge() {
  const { pathname, search } = useLocation();
  const { currentWorkspaceId, setCurrentWorkspaceId } = useWorkspace();
  const { currentFilmId, setCurrentFilmId } = useFilm();
  const scoped = useScopedNav();

  const ids = useMemo(() => extractIdsFromPath(pathname), [pathname]);

  // URL → context (one-way). Guarded so we only setState when value changes.
  useEffect(() => {
    if (ids.workspaceId && ids.workspaceId !== currentWorkspaceId) {
      setCurrentWorkspaceId(ids.workspaceId);
    }
  }, [ids.workspaceId, currentWorkspaceId, setCurrentWorkspaceId]);

  useEffect(() => {
    if (ids.filmId && ids.filmId !== currentFilmId) {
      setCurrentFilmId(ids.filmId);
    }
  }, [ids.filmId, currentFilmId, setCurrentFilmId]);

  // Dev/test debug overlay — read-only snapshot exposed on window.
  // Safe to leave in prod: it's just a small object.
  const snapshotRef = useRef({});
  useEffect(() => {
    const snap = {
      pathname,
      search,
      currentWorkspaceId: currentWorkspaceId || null,
      currentFilmId: currentFilmId || null,
      urlWorkspaceId: ids.workspaceId || null,
      urlFilmId: ids.filmId || null,
      scope: scoped.scope,
      canAccess: scoped.canAccess,
      reason: scoped.reason,
      scopedNavSnapshot: {
        dashboard: scoped.to.dashboard(),
        projects: scoped.to.projects(),
        calendar: scoped.to.calendar(),
        gear: scoped.to.gear(),
        files: scoped.to.files(),
        scenes: scoped.to.scenes(),
        shots: scoped.to.shots(),
        team: scoped.to.team(),
        settings: scoped.to.settings(),
      },
    };
    snapshotRef.current = snap;
    if (typeof window !== 'undefined') {
      window.__frameflowCtx = snap;
    }
  }, [pathname, search, currentWorkspaceId, currentFilmId, ids.workspaceId, ids.filmId, scoped]);

  return null;
}
